import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { removeToken } from '../utils/auth';
import { authService } from '../services';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    console.log('🚪 [AUTH] Logout initiated');
    console.log('🗑️ [AUTH] Removing token and clearing user data');
    removeToken();
    setIsAuthenticated(false);
    setUser(null);
    console.log('✅ [AUTH] Logout completed');
  }, []);

  const checkAuthStatus = useCallback(async () => {
    console.log('🔍 [AUTH] Starting authentication validation...');

    try {
      // ✅ With httpOnly cookie, token is sent automatically by browser
      // No need to check client-side - server validates via cookie
      console.log('✅ [AUTH] Checking user status via API (httpOnly cookie sent automatically)...');

      // Use the authService to check current user
      const authResult = await authService.checkAuthStatus();

      console.log('🔍 [AUTH] Auth service response:', {
        authenticated: authResult.authenticated,
        user: authResult.user ? `${authResult.user.name} (ID: ${authResult.user.id})` : null
      });

      if (authResult.authenticated && authResult.user) {
        console.log('✅ [AUTH] User authentication successful');
        console.log('👤 [AUTH] User data:', {
          id: authResult.user.id,
          name: authResult.user.name,
          email: authResult.user.email
        });
        setIsAuthenticated(true);
        setUser(authResult.user);
      } else {
        console.log('❌ [AUTH] User authentication failed or user data missing');
        logout();
      }
    } catch (error) {
      console.error('❌ [AUTH] Auth check failed with error:', error);
      logout();
    } finally {
      console.log('🔚 [AUTH] Authentication validation complete');
      setLoading(false);
    }
  }, [logout]);

  // Check authentication status on mount and browser navigation
  useEffect(() => {
    checkAuthStatus();

    // Re-check auth when user navigates via browser back/forward
    const handlePopState = () => {
      console.log('🔄 [AUTH] Browser navigation detected, re-checking auth...');
      checkAuthStatus();
    };

    // Re-check auth when page becomes visible (e.g., returning to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 [AUTH] Page visible, re-checking auth...');
        checkAuthStatus();
      }
    };

    // Re-check auth when window gains focus (more immediate than visibility change)
    const handleFocus = () => {
      console.log('🔄 [AUTH] Window focused, re-checking auth...');
      checkAuthStatus();
    };

    // Re-check auth when page loads from bfcache (browser back/forward cache)
    const handlePageShow = (event) => {
      if (event.persisted) {
        console.log('🔄 [AUTH] Page loaded from bfcache, re-checking auth...');
        checkAuthStatus();
      }
    };

    // Periodic refresh to catch external profile changes (e.g., from users dashboard)
    // Check every 30 seconds if authenticated
    const refreshInterval = setInterval(() => {
      if (!document.hidden && isAuthenticated) {
        console.log('🔄 [AUTH] Periodic refresh - checking for profile updates...');
        checkAuthStatus();
      }
    }, 30000); // 30 seconds

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, [checkAuthStatus, isAuthenticated]);

  const login = async (credentials) => {
    console.log('🔐 [AUTH] Login attempt started for:', credentials.email);
    
    try {
      setLoading(true);
      const response = await authService.login(credentials);
      
      console.log('🔍 [AUTH] Login response:', { 
        message: response.message, 
        hasError: !!response.error 
      });
      
      if (response.message === 'Success') {
        console.log('✅ [AUTH] Login successful - refreshing auth status...');
        // After successful login, check auth status to set user data
        await checkAuthStatus();
        console.log('🎉 [AUTH] Login process completed successfully');
        return { success: true, message: response.message };
      } else {
        console.log('❌ [AUTH] Login failed:', response.error);
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('❌ [AUTH] Login error:', error);
      return { success: false, error: 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  const refreshAuth = async () => {
    console.log('🔄 [AUTH] Manual auth refresh requested');
    await checkAuthStatus();
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    refreshAuth,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};